import React, { useRef, useMemo, useState } from 'react';
import { Box,
	Table,
	TableHead,
	TableCell,
	TableRow,
	TableBody,
	TablePagination,
	FormControlLabel,
	Checkbox } from '@mui/material';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';
import PropTypes from 'prop-types';
import { Tooltip } from '../../../components/Tooltip';

function CsvTable({
	rows,
	headers,
	dataIssues,
	displayIgnore,
}) {
	const [page, setPage] = useState(0);
	const [hideDuplicates, setHideDuplicates] = useState(false);
	const [rowsPerPage, setRowsPerPage] = useState(10);
	const filteredRows = useMemo(() => {
		return rows.map((row, index) => {
			const issues = dataIssues[index]?.row || [];
			const isDuplicateRow = issues.some((issue) => issue.error === 'DUPLICATE_ROW');
			return { row, index, isDuplicateRow };
		}).filter(({ isDuplicateRow }) => {
			return hideDuplicates ? !isDuplicateRow : true;
		});
	}, [rows, dataIssues, hideDuplicates]);

	const currentPageRows = filteredRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

	return (
		<>

			<Box
				sx={{
					position: 'relative',
					pt: 3,
					pr: 3,
					pl: 3,
					overflow: 'auto',
					// Scroll shadow
					background: `linear-gradient(to right, #fff 30%, rgba(255,255,255,0)),
						linear-gradient(to right, rgba(255,255,255,0), #fff 70%) 100% 0,
						radial-gradient(farthest-side at 0 50%, rgba(0,0,0,0.4), rgba(0,0,0,0)),
						radial-gradient(farthest-side at 100% 50%, rgba(0,0,0,0.4), rgba(0,0,0,0)) 100% 0;`,
					backgroundPosition: 'left center, right center, left center, right center',
					backgroundRepeat: 'no-repeat',
					backgroundColor: 'white',
					backgroundSize: '20px 100%, 20px 100%, 10px 100%, 10px 100%',
					backgroundAttachment: 'local, local, scroll, scroll',
				}}
			>
				<Table
					sx={{
						minWidth: 650,
					}}
					aria-label="table to import"
				>
					<TableHead>
						<TableRow>
							{Object.entries(headers).map(([key, header]) => (
								<TableCell
									component="th"
									sx={{ textAlign: 'left', whiteSpace: 'nowrap' }}
									key={key}
								>
									{header.renderHeader ? header.renderHeader() : header.label}
								</TableCell>
							))}
						</TableRow>
					</TableHead>
					<TableBody>
						{currentPageRows.map(({ row, index: realIndex, isDuplicateRow }, index) => {
							return (
								<TableRow
									// eslint-disable-next-line react/no-array-index-key
									key={index}
									sx={{
										'&:last-child td, &:last-child th': { border: 0 },
									}}
								>
									{Object.keys(headers).map((key) => {
										const issues = dataIssues?.[realIndex]?.[key] ?? [];
										const groupedMessages = issues.reduce((acc, issue) => {
											const { type, message } = issue;
											if (!acc[type]) acc[type] = [];
											acc[type].push(message);
											return acc;
										}, {});

										const format = (list) => (list?.length ? `${list[0]}${list.length > 1 ? ` + ${list.length - 1} more` : ''}` : null);

										const {
											error: errorsToDisplay,
											warning: warningsToDisplay,
											ignore: ignoresToDisplay,
										} = Object.fromEntries(
											Object.entries(groupedMessages).map(([type, messages]) => [type, format(messages)]),
										);
										return (
											<TableCell
												key={key}
												component="td"
												scope="row"
												sx={{
													textAlign: 'left',
													color: (ignoresToDisplay?.length > 0 || isDuplicateRow) ? 'lightgray' : 'inherit',
												}}
												title={row[key]}
											>
												<Box
													sx={{
														position: 'relative',
														width: 150,
														display: 'flex',
														alignItems: 'center',
														overflow: 'hidden',
													}}
												>
													{/* Text content with ellipsis and padding for icon */}
													<Box
														sx={{
															whiteSpace: 'nowrap',
															overflow: 'hidden',
															textOverflow: 'ellipsis',
															paddingRight: 2.5, // Reserve space for icon
															flex: 1,
														}}
													>
														{row[key] || 'N/A'}
													</Box>

													<Box
														sx={{
															position: 'absolute',
															right: 4,
															top: '50%',
															transform: 'translateY(-50%)',
															display: 'flex',
															alignItems: 'center',
															gap: 0.5,
														}}
													>
														{errorsToDisplay && (
															<Tooltip title={errorsToDisplay}>
																<ErrorIcon sx={{ color: 'error.main', fontSize: 18 }} />
															</Tooltip>
														)}
														{warningsToDisplay && (
															<Tooltip title={warningsToDisplay}>
																<WarningIcon sx={{ color: 'warning.main', fontSize: 18 }} />
															</Tooltip>
														)}
														{displayIgnore && ignoresToDisplay && (
															<Tooltip title={ignoresToDisplay}>
																<WarningIcon sx={{ color: 'info.main', fontSize: 18 }} />
															</Tooltip>
														)}
													</Box>
												</Box>
											</TableCell>

										);
									})}
								</TableRow>
							);
						})}
					</TableBody>
				</Table>

			</Box>
			<Box
				sx={{
					display: 'flex',
					justifyContent: 'space-between', // Or 'flex-start' if you want them grouped on the left
					alignItems: 'center',
					px: 2,
					py: 1,
				}}
			>
				<FormControlLabel
					control={(
						<Checkbox
							checked={hideDuplicates}
							onChange={(e) => {
								setHideDuplicates(e.target.checked);
								setPage(0); // Reset to first page
							}}
						/>
					)}
					label="Hide ignored rows"
				/>
				<TablePagination
					component="div"
					count={filteredRows.length}
					page={page}
					rowsPerPage={rowsPerPage}
					onPageChange={(__, newPage) => {
						setPage(newPage);
					}}
					onRowsPerPageChange={(e) => {
						setRowsPerPage(e.target.value);
						setPage(Math.floor((page * rowsPerPage) / e.target.value));
					}}
				/>
			</Box>

		</>
	);
}

CsvTable.defaultProps = {
	displayIgnore: false,
};

CsvTable.propTypes = {
	rows: PropTypes.arrayOf(PropTypes.object).isRequired,
	headers: PropTypes.objectOf(
		PropTypes.shape({
			label: PropTypes.string.isRequired,
			renderHeader: PropTypes.func,
		}),
	).isRequired,
	dataIssues: PropTypes.objectOf(PropTypes.shape({
		type: PropTypes.oneOf(['error', 'warning', 'ignore']),
		message: PropTypes.string,
		error: PropTypes.string,
	})).isRequired,
	displayIgnore: PropTypes.bool,
};

export default CsvTable;
